import DropdownInput from "components/common/form/inputs/dropdown/DropdownInput";
import { DataSourceRequestType } from "types/datalist-types";
import { DropdownInputPropsType } from "types/form-types";
import { PersonFilterInput, PersonListInfoQueryVariables, PersonSortInput, SortEnumType } from "types/graphql";
import { personListInfo, PersonType } from "types/person-types";
import * as lodash from 'lodash';
import { useTranslation } from "react-i18next";
import ModalPortal from "components/modal/ModalPortal";
import PersonFrom from "../../PersonFrom";
import useAppContext from "utilities/hooks/useAppContext";
import { Roles } from "utilities/app-utilities";
import { appendFilters, resolveSearchFilterPropertyName } from "utilities/datalist/datalist-filter-utilities";
import If from "components/common/If";
import HTMLCode from "components/common/HTMLCode";

export const ADD_PERSON_ID = -1;
const ADD_PERSON_LINK = 'person-drop-down-form';

export type PersonDropdownPropsType = DropdownInputPropsType<PersonType, PersonFilterInput, PersonSortInput> & {
    addPerson?: {
        // onSubmit?: (person: PersonType) => void,
        id?: number,
        model?: Partial<PersonType>
    } | boolean,
    search?: 'on' | 'switch' | 'off'
    take?: number,
    where?: PersonFilterInput | undefined
}

const PersonDropdown = (props: Partial<PersonDropdownPropsType>) => {
    const [t] = useTranslation();
    const context = useAppContext();

    const dropdownPersonConfig: PersonDropdownPropsType = {
        getItemId: i => i.id,
        required: true,
        dataSource: {
            request: async (params: DataSourceRequestType<PersonFilterInput, PersonSortInput>) => {
                let variables: PersonListInfoQueryVariables = {
                    take: params.take,
                    order: params.sort || [
                        {
                            name: SortEnumType.Asc
                        }
                    ]
                };

                if (params.searchText) {
                    const nameFilter = resolveSearchFilterPropertyName([], params.searchText);
                    const phoneFilter: PersonFilterInput = {
                        contactInfo: {
                            some: {
                                value: {
                                    contains: params.searchText.replace(" ", "")
                                }
                            }
                        }
                    }

                    const personFilter: PersonFilterInput = {
                        or: [
                            nameFilter,
                            phoneFilter
                        ]
                    };

                    variables.where = appendFilters(variables.where, personFilter);
                }

                if (props.where) {
                    variables.where = appendFilters(variables.where, props.where);
                }

                const data = await personListInfo(variables);
                if (props.addPerson && context.userHasRole(Roles.PERSON_WRITE)) {
                    data.data.items.unshift({
                        id: (typeof props.addPerson == 'object' && props.addPerson.id) || ADD_PERSON_ID,
                        name: '-- ' + t('button.add-new'),
                        contactInfo: [],
                        tags: []
                    })
                }

                return data.data.items;
            },
            search: props.search || 'on',
            take: props.take
        },
        view: {
            inputView: i => i?.name,
            dropdownView: i => {
                return <div key={`dropdown-i-${i.id}`}>
                    <div><b>{i.name}</b></div>
                    <div className="text-sm"><i>{i.contactInfo.filter(x => x.value).map(c => {
                        return c.value;
                    }).join(' - ')}</i></div>
                    <If condition={i.country}>
                        <div className="flex text-sm">
                            <img src={i.country?.flag} className="max-w-4 max-h-4 mt-1" alt={i.country?.name} />
                            <HTMLCode />
                            <span>{i.country?.name}</span>
                        </div>
                    </If>
                    <div className="text-sm"><i>{i.tags.map(x => x.name).join(', ')}</i></div>
                </div>
            }
        },
        label: 'fields.person.name'
    };

    const settings = lodash.merge(dropdownPersonConfig, props);

    const onChange = (s: PersonType | undefined, event: React.MouseEvent | React.KeyboardEvent | React.FocusEvent) => {
        if (props.addPerson && s && s.id === (props.id || ADD_PERSON_ID)) {
            context.modal.open(ADD_PERSON_LINK);
        } else if (props.onChange) {
            props.onChange(s, event);
        }
    }

    const onPersonFormSubmit = (model: PersonType | undefined, _: any, event?: React.FormEvent) => {
        event?.preventDefault();
        if (model) onChange(model, undefined as any);
        context.modal.close(ADD_PERSON_LINK);
    }

    return (
        <>
            <DropdownInput
                {...settings}
                value={props.value}
                onChange={onChange}
            />
            <ModalPortal name={ADD_PERSON_LINK} title="pages.people.form">
                <PersonFrom
                    model={props.addPerson && typeof props.addPerson == 'object' ? props.addPerson.model : undefined}
                    onSubmit={onPersonFormSubmit}
                    name="person-drop-down-form"
                />
            </ModalPortal>
        </>
    );
};

export default PersonDropdown;
